/**
 * services/LiveTradingSettingsService.ts
 * Live trading dashboard configuration management service
 *
 * Provides singleton read/write access to `LiveTradingSettings`, including
 * layout configuration (presets, working layout, pane weights, banner config)
 * and saved layout CRUD operations (T12: create, duplicate, rename, delete, apply, save).
 *
 * Follows the established settings-service pattern:
 * - Extends `BaseService<LiveTradingSettings>`
 * - `getOrCreateDefaultSettings()` ensures a valid record always exists
 * - Migration chain backfills new fields on existing records
 *
 * @see types/schema/LiveTradingSettings.ts for the entity type
 * @see types/shared/AlarmRule.ts for alarm rule types
 * @see config/defaults.ts for DEFAULT_LIVE_TRADING_SETTINGS and createDefaultLayoutConfig()
 * @see constants/layout.ts for preset definitions and layout constants
 */

import { BaseService } from './BaseService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import type { LiveTradingSettings } from '@/types/schema/LiveTradingSettings';
import type { LayoutConfig, SavedLayout, WorkingLayout } from '@/types/shared/Layout';
import { generateUUID } from '@/utils/uuid';
import { DEFAULT_LIVE_TRADING_SETTINGS, createDefaultLayoutConfig } from '@/config/defaults';
import { DEFAULT_LAYOUT_SCHEMA_VERSION, DEFAULT_SLOT_ASSIGNMENTS } from '@/constants/layout';

/**
 * Service for managing live trading dashboard settings
 *
 * Extends BaseService to provide standard CRUD operations plus
 * specialized methods for singleton settings access and layout
 * configuration updates.
 */
export class LiveTradingSettingsService extends BaseService<LiveTradingSettings> {
  constructor() {
    super('live_trading_settings');
  }

  /**
   * Get default live trading settings with layout configuration
   *
   * Spreads static field defaults from `DEFAULT_LIVE_TRADING_SETTINGS`
   * and adds a fresh `layoutConfig` via factory function (real timestamp).
   *
   * @returns Default settings object (without id, createdAt, updatedAt)
   * @private
   */
  private getDefaultSettings(): Omit<
    LiveTradingSettings,
    'id' | 'createdAt' | 'updatedAt' | 'deletedAt' | 'deletedByDevice'
  > {
    return {
      ...DEFAULT_LIVE_TRADING_SETTINGS,
      layoutConfig: createDefaultLayoutConfig(),
    };
  }

  /**
   * Get existing settings or create default settings if none exist
   *
   * This is the primary method for retrieving live trading settings.
   * On first use, it creates a default settings record. On subsequent
   * calls, it returns the existing singleton and runs any pending
   * migrations (e.g., backfilling `layoutConfig` on pre-T09 records).
   *
   * @returns Promise resolving to LiveTradingSettings with layoutConfig guaranteed present
   */
  async getOrCreateDefaultSettings(): Promise<LiveTradingSettings> {
    try {
      // Check if settings already exist (getAll excludes tombstones)
      const allSettings = await this.getAll();

      if (allSettings.length > 0) {
        // Use first settings record (singleton semantics)
        const firstSetting = allSettings[0];
        if (!firstSetting) {
          throw new Error('Unexpected undefined settings record');
        }

        // Chain migrations: each operates on the result of the previous
        const afterLayoutMigration = await this.migrateLayoutConfig(firstSetting);
        const afterSavedLayoutsMigration = await this.migrateSavedLayouts(afterLayoutMigration);
        const afterAutosaveMigration = await this.migrateAutosaveEnabled(afterSavedLayoutsMigration);
        const afterAlarmMigration = await this.migrateAlarmConfig(afterAutosaveMigration);
        return afterAlarmMigration;
      }

      // No settings exist, create default
      const defaultSettings = this.getDefaultSettings();
      const createdSettings = await this.add(defaultSettings);

      return createdSettings;
    } catch (error) {
      console.error('Failed to get or create live trading settings:', error);
      throw new Error('Failed to initialize live trading settings');
    }
  }

  /**
   * Migrate existing records to include layoutConfig and slotAssignments
   *
   * Migration chain:
   * - T09: Pre-T09 records won't have `layoutConfig` — backfill with default
   * - T10: Pre-T10 records may have `layoutConfig` but no `slotAssignments` —
   *   backfill with DEFAULT_SLOT_ASSIGNMENTS
   *
   * Only persists if a change was made to avoid unnecessary writes.
   *
   * @param settings - Current settings to check and migrate
   * @returns Settings with layoutConfig and slotAssignments guaranteed present
   * @private
   */
  private async migrateLayoutConfig(settings: LiveTradingSettings): Promise<LiveTradingSettings> {
    // T09 migration: backfill missing layoutConfig entirely
    const existingConfig = (settings as { layoutConfig?: LayoutConfig }).layoutConfig;

    if (!existingConfig) {
      // Backfill with fresh default (includes slotAssignments via factory)
      const defaultLayoutConfig = createDefaultLayoutConfig();
      const updatedSettings = await this.update(settings.id, {
        layoutConfig: defaultLayoutConfig,
      } as Partial<LiveTradingSettings>);
      return updatedSettings;
    }

    // T10 migration: backfill missing slotAssignments on existing layoutConfig
    if (!existingConfig.workingLayout.slotAssignments) {
      const updatedConfig: LayoutConfig = {
        ...existingConfig,
        workingLayout: {
          ...existingConfig.workingLayout,
          slotAssignments: { ...DEFAULT_SLOT_ASSIGNMENTS },
          updatedAt: new Date().toISOString(),
        },
      };
      const updatedSettings = await this.update(settings.id, {
        layoutConfig: updatedConfig,
      } as Partial<LiveTradingSettings>);
      return updatedSettings;
    }

    return settings;
  }

  /**
   * Migrate layout config to schema version 2 (T12: saved layouts)
   *
   * Backfills `savedLayouts: []` when absent and bumps `schemaVersion`
   * to `DEFAULT_LAYOUT_SCHEMA_VERSION` when stale. Idempotent — records
   * that already have `savedLayouts` and current schema version are untouched.
   *
   * @param settings - Current settings (layoutConfig guaranteed present after T09)
   * @returns Settings with savedLayouts and current schemaVersion guaranteed
   * @private
   */
  private async migrateSavedLayouts(settings: LiveTradingSettings): Promise<LiveTradingSettings> {
    const existingConfig = settings.layoutConfig as LayoutConfig;

    const missingSavedLayouts = !existingConfig.savedLayouts;
    const staleSchema = existingConfig.schemaVersion < DEFAULT_LAYOUT_SCHEMA_VERSION;

    if (!missingSavedLayouts && !staleSchema) {
      return settings;
    }

    const updatedConfig: LayoutConfig = {
      ...existingConfig,
      ...(missingSavedLayouts ? { savedLayouts: [] } : {}),
      schemaVersion: DEFAULT_LAYOUT_SCHEMA_VERSION,
    };

    return this.update(settings.id, {
      layoutConfig: updatedConfig,
    } as Partial<LiveTradingSettings>);
  }

  /**
   * Migrate layout config to schema version 3 (T12C: autosave toggle)
   *
   * Backfills `autosaveEnabled: true` when absent and bumps `schemaVersion`
   * to `DEFAULT_LAYOUT_SCHEMA_VERSION` when stale. Idempotent — records
   * that already have `autosaveEnabled` set and current schema version are
   * untouched. An existing `false` value is never overwritten.
   *
   * @param settings - Current settings (layoutConfig guaranteed present after T09)
   * @returns Settings with autosaveEnabled and current schemaVersion guaranteed
   * @private
   */
  private async migrateAutosaveEnabled(
    settings: LiveTradingSettings,
  ): Promise<LiveTradingSettings> {
    const existingConfig = settings.layoutConfig as LayoutConfig;

    const missingAutosave = existingConfig.autosaveEnabled === undefined;
    const staleSchema = existingConfig.schemaVersion < DEFAULT_LAYOUT_SCHEMA_VERSION;

    if (!missingAutosave && !staleSchema) {
      return settings;
    }

    const updatedConfig: LayoutConfig = {
      ...existingConfig,
      ...(missingAutosave ? { autosaveEnabled: true } : {}),
      schemaVersion: DEFAULT_LAYOUT_SCHEMA_VERSION,
    };

    return this.update(settings.id, {
      layoutConfig: updatedConfig,
    } as Partial<LiveTradingSettings>);
  }

  /**
   * Update the layout configuration (singleton write API)
   *
   * Resolves the singleton settings record internally — callers never
   * need to track the settings ID. Replaces the entire `layoutConfig`
   * object; the caller is responsible for merging before calling.
   *
   * @param layoutConfig - Complete layout configuration to persist
   * @returns Promise resolving to updated LiveTradingSettings
   */
  async updateLayoutConfig(layoutConfig: LayoutConfig): Promise<LiveTradingSettings> {
    const settings = await this.getOrCreateDefaultSettings();

    return this.update(settings.id, {
      layoutConfig,
    } as Partial<LiveTradingSettings>);
  }

  // ===== Saved Layout CRUD (T12) =====

  /**
   * Snapshot the current working layout into a new SavedLayout object.
   * Shared by createSavedLayout and duplicateSavedLayout.
   *
   * @param name - Pre-validated (trimmed, non-empty) layout name
   * @param workingLayout - Current working layout to snapshot
   * @returns New SavedLayout with generated UUID and timestamps
   * @private
   */
  private snapshotToSavedLayout(name: string, workingLayout: WorkingLayout): SavedLayout {
    const now = new Date().toISOString();
    // Deep-clone bannerConfig (nested widgets/widgetConfigs); shallow-clone flat records.
    // structuredClone available in all target browsers (Chrome 98+, Safari 15.4+, Firefox 94+).
    return {
      id: generateUUID(),
      name,
      presetId: workingLayout.presetId,
      bannerConfig: structuredClone(workingLayout.bannerConfig),
      ...(workingLayout.paneWeights ? { paneWeights: { ...workingLayout.paneWeights } } : {}),
      ...(workingLayout.slotAssignments ? { slotAssignments: { ...workingLayout.slotAssignments } } : {}),
      createdAt: now,
      updatedAt: now,
    };
  }

  /**
   * Validate and trim a layout name.
   *
   * @param name - Raw name input
   * @returns Trimmed name
   * @throws Error if name is empty or whitespace-only
   * @private
   */
  private validateLayoutName(name: string): string {
    const trimmed = name.trim();
    if (!trimmed) {
      throw new Error('Layout name cannot be empty');
    }
    return trimmed;
  }

  /**
   * Create a new saved layout from the current working layout.
   * Snapshots the workspace configuration and sets it as the active layout.
   *
   * @param name - User-assigned layout name (will be trimmed)
   * @returns Updated LayoutConfig with new layout appended and set as active
   * @throws Error if name is empty
   */
  async createSavedLayout(name: string): Promise<LayoutConfig> {
    const trimmedName = this.validateLayoutName(name);
    const settings = await this.getOrCreateDefaultSettings();
    const existingConfig = settings.layoutConfig as LayoutConfig;

    const newLayout = this.snapshotToSavedLayout(trimmedName, existingConfig.workingLayout);

    const updatedConfig: LayoutConfig = {
      ...existingConfig,
      savedLayouts: [...(existingConfig.savedLayouts ?? []), newLayout],
      activeLayoutId: newLayout.id,
    };

    const updatedSettings = await this.update(settings.id, {
      layoutConfig: updatedConfig,
    } as Partial<LiveTradingSettings>);
    return updatedSettings.layoutConfig as LayoutConfig;
  }

  /**
   * Duplicate the current working layout under a new name.
   * Functionally identical to createSavedLayout — the distinction
   * exists for UX clarity ("Make a copy" vs "Create new layout").
   *
   * @param newName - Name for the duplicated layout (will be trimmed)
   * @returns Updated LayoutConfig with duplicated layout appended and set as active
   * @throws Error if name is empty
   */
  async duplicateSavedLayout(newName: string): Promise<LayoutConfig> {
    return this.createSavedLayout(newName);
  }

  /**
   * Duplicate an existing saved layout by ID under a new name.
   * Clones the target layout's snapshot (not the current working state)
   * and sets the copy as the active layout.
   *
   * @param layoutId - ID of the saved layout to duplicate
   * @param newName - Name for the duplicated layout (will be trimmed)
   * @returns Updated LayoutConfig with duplicated layout appended and set as active
   * @throws Error if name is empty or layout not found
   */
  async duplicateSavedLayoutById(layoutId: string, newName: string): Promise<LayoutConfig> {
    const trimmedName = this.validateLayoutName(newName);
    const settings = await this.getOrCreateDefaultSettings();
    const existingConfig = settings.layoutConfig as LayoutConfig;
    const savedLayouts = existingConfig.savedLayouts ?? [];

    const targetLayout = savedLayouts.find((l) => l.id === layoutId);
    if (!targetLayout) {
      throw new Error(`Saved layout not found: ${layoutId}`);
    }

    // Clone snapshot fields from the target saved layout (not working layout).
    // Same cloning strategy as snapshotToSavedLayout: structuredClone for
    // bannerConfig (nested), conditional spread for flat records.
    const now = new Date().toISOString();
    const newLayout: SavedLayout = {
      id: generateUUID(),
      name: trimmedName,
      presetId: targetLayout.presetId,
      bannerConfig: structuredClone(targetLayout.bannerConfig),
      ...(targetLayout.paneWeights ? { paneWeights: { ...targetLayout.paneWeights } } : {}),
      ...(targetLayout.slotAssignments ? { slotAssignments: { ...targetLayout.slotAssignments } } : {}),
      createdAt: now,
      updatedAt: now,
    };

    const updatedConfig: LayoutConfig = {
      ...existingConfig,
      savedLayouts: [...savedLayouts, newLayout],
      activeLayoutId: newLayout.id,
    };

    const updatedSettings = await this.update(settings.id, {
      layoutConfig: updatedConfig,
    } as Partial<LiveTradingSettings>);
    return updatedSettings.layoutConfig as LayoutConfig;
  }

  /**
   * Rename an existing saved layout.
   *
   * @param layoutId - ID of the layout to rename
   * @param newName - New display name (will be trimmed)
   * @returns Updated LayoutConfig with renamed layout
   * @throws Error if name is empty or layout not found
   */
  async renameSavedLayout(layoutId: string, newName: string): Promise<LayoutConfig> {
    const trimmedName = this.validateLayoutName(newName);
    const settings = await this.getOrCreateDefaultSettings();
    const existingConfig = settings.layoutConfig as LayoutConfig;
    const savedLayouts = existingConfig.savedLayouts ?? [];

    const targetLayout = savedLayouts.find((l) => l.id === layoutId);
    if (!targetLayout) {
      throw new Error(`Saved layout not found: ${layoutId}`);
    }

    const updatedConfig: LayoutConfig = {
      ...existingConfig,
      savedLayouts: savedLayouts.map((l) =>
        l.id === layoutId
          ? { ...l, name: trimmedName, updatedAt: new Date().toISOString() }
          : l,
      ),
    };

    const updatedSettings = await this.update(settings.id, {
      layoutConfig: updatedConfig,
    } as Partial<LiveTradingSettings>);
    return updatedSettings.layoutConfig as LayoutConfig;
  }

  /**
   * Delete a saved layout. If the deleted layout is active,
   * clears activeLayoutId (reverts to unsaved mode).
   *
   * @param layoutId - ID of the layout to delete
   * @returns Updated LayoutConfig without the deleted layout
   * @throws Error if layout not found
   */
  async deleteSavedLayout(layoutId: string): Promise<LayoutConfig> {
    const settings = await this.getOrCreateDefaultSettings();
    const existingConfig = settings.layoutConfig as LayoutConfig;
    const savedLayouts = existingConfig.savedLayouts ?? [];

    const targetLayout = savedLayouts.find((l) => l.id === layoutId);
    if (!targetLayout) {
      throw new Error(`Saved layout not found: ${layoutId}`);
    }

    const filteredLayouts = savedLayouts.filter((l) => l.id !== layoutId);

    // If deleting the active layout, clear activeLayoutId (exactOptionalPropertyTypes)
    if (existingConfig.activeLayoutId === layoutId) {
      const { activeLayoutId: _, ...configWithoutActiveId } = existingConfig;
      const updatedConfig: LayoutConfig = {
        ...configWithoutActiveId,
        savedLayouts: filteredLayouts,
      };
      const updatedSettings = await this.update(settings.id, {
        layoutConfig: updatedConfig,
      } as Partial<LiveTradingSettings>);
      return updatedSettings.layoutConfig as LayoutConfig;
    }

    const updatedConfig: LayoutConfig = {
      ...existingConfig,
      savedLayouts: filteredLayouts,
    };

    const updatedSettings = await this.update(settings.id, {
      layoutConfig: updatedConfig,
    } as Partial<LiveTradingSettings>);
    return updatedSettings.layoutConfig as LayoutConfig;
  }

  /**
   * Apply a saved layout to the workspace. Replaces the working layout
   * with the saved snapshot and syncs activePresetId for renderer consistency.
   *
   * @param layoutId - ID of the layout to apply
   * @returns Updated LayoutConfig with applied working layout
   * @throws Error if layout not found
   */
  async applySavedLayout(layoutId: string): Promise<LayoutConfig> {
    const settings = await this.getOrCreateDefaultSettings();
    const existingConfig = settings.layoutConfig as LayoutConfig;
    const savedLayouts = existingConfig.savedLayouts ?? [];

    const targetLayout = savedLayouts.find((l) => l.id === layoutId);
    if (!targetLayout) {
      throw new Error(`Saved layout not found: ${layoutId}`);
    }

    // Clone snapshot fields to avoid shared references between workingLayout and savedLayouts
    const newWorkingLayout: WorkingLayout = {
      presetId: targetLayout.presetId,
      bannerConfig: structuredClone(targetLayout.bannerConfig),
      ...(targetLayout.paneWeights ? { paneWeights: { ...targetLayout.paneWeights } } : {}),
      ...(targetLayout.slotAssignments ? { slotAssignments: { ...targetLayout.slotAssignments } } : {}),
      updatedAt: new Date().toISOString(),
    };

    const updatedConfig: LayoutConfig = {
      ...existingConfig,
      activePresetId: targetLayout.presetId,
      workingLayout: newWorkingLayout,
      activeLayoutId: layoutId,
    };

    const updatedSettings = await this.update(settings.id, {
      layoutConfig: updatedConfig,
    } as Partial<LiveTradingSettings>);
    return updatedSettings.layoutConfig as LayoutConfig;
  }

  /**
   * Save the current working layout state to the active saved layout.
   * No-ops if no layout is active. Clears stale activeLayoutId references.
   *
   * @returns Updated LayoutConfig (unchanged if no active layout)
   */
  async saveToActiveLayout(): Promise<LayoutConfig> {
    const settings = await this.getOrCreateDefaultSettings();
    const existingConfig = settings.layoutConfig as LayoutConfig;

    // No active layout — no-op
    if (!existingConfig.activeLayoutId) {
      return existingConfig;
    }

    const savedLayouts = existingConfig.savedLayouts ?? [];
    const activeLayout = savedLayouts.find((l) => l.id === existingConfig.activeLayoutId);

    // Stale reference — clear activeLayoutId and persist cleanup
    if (!activeLayout) {
      const { activeLayoutId: _, ...configWithoutActiveId } = existingConfig;
      const cleanedConfig: LayoutConfig = { ...configWithoutActiveId };
      const updatedSettings = await this.update(settings.id, {
        layoutConfig: cleanedConfig,
      } as Partial<LiveTradingSettings>);
      return updatedSettings.layoutConfig as LayoutConfig;
    }

    // Overwrite the active layout's snapshot with current working layout
    const now = new Date().toISOString();
    const workingLayout = existingConfig.workingLayout;

    // Rebuild matched layout from scratch: preserve identity fields, take snapshot
    // fields exclusively from workingLayout (avoids stale optional fields from ...l)
    const updatedConfig: LayoutConfig = {
      ...existingConfig,
      savedLayouts: savedLayouts.map((l) =>
        l.id === existingConfig.activeLayoutId
          ? {
              id: l.id,
              name: l.name,
              createdAt: l.createdAt,
              presetId: workingLayout.presetId,
              bannerConfig: structuredClone(workingLayout.bannerConfig),
              ...(workingLayout.paneWeights ? { paneWeights: { ...workingLayout.paneWeights } } : {}),
              ...(workingLayout.slotAssignments ? { slotAssignments: { ...workingLayout.slotAssignments } } : {}),
              updatedAt: now,
            }
          : l,
      ),
    };

    const updatedSettings = await this.update(settings.id, {
      layoutConfig: updatedConfig,
    } as Partial<LiveTradingSettings>);
    return updatedSettings.layoutConfig as LayoutConfig;
  }

  /**
   * Migrate existing records to include valid alarm configuration
   *
   * T14 migration: backfill missing or malformed alarm fields with defaults.
   * Defensive normalization ensures Phase 3+ can assume valid shapes:
   * - Non-array alarmRules → []
   * - Non-number / negative / NaN alarmAutoDismissMs → 15000
   *
   * Only persists if a correction was actually made to avoid unnecessary writes.
   *
   * @param settings - Current settings to check and migrate
   * @returns Settings with valid alarm fields guaranteed
   * @private
   */
  private async migrateAlarmConfig(settings: LiveTradingSettings): Promise<LiveTradingSettings> {
    const updates: Partial<LiveTradingSettings> = {};
    let needsUpdate = false;

    // Backfill or coerce alarmRules (widen to unknown for defensive runtime check)
    const rawRules: unknown = settings.alarmRules;
    if (!Array.isArray(rawRules)) {
      updates.alarmRules = [];
      needsUpdate = true;
    }

    // Backfill or coerce alarmAutoDismissMs (widen to unknown for defensive runtime check)
    const rawDismiss: unknown = settings.alarmAutoDismissMs;
    if (typeof rawDismiss !== 'number' || !Number.isFinite(rawDismiss) || rawDismiss < 0) {
      updates.alarmAutoDismissMs = 15000;
      needsUpdate = true;
    }

    if (needsUpdate) {
      return this.update(settings.id, updates as Partial<LiveTradingSettings>);
    }

    return settings;
  }

  /**
   * Update alarm configuration (singleton write API)
   *
   * Resolves the singleton settings record internally. Accepts any subset
   * of alarmRules and alarmAutoDismissMs.
   *
   * After commit, fires both local (same-tab) and cross-tab notifications
   * so the alarm engine picks up changes without page reload.
   *
   * @param updates - Alarm-related fields to update
   * @returns Promise resolving to updated LiveTradingSettings
   */
  async updateAlarmConfig(
    updates: Partial<Pick<LiveTradingSettings, 'alarmRules' | 'alarmAutoDismissMs'>>,
  ): Promise<LiveTradingSettings> {
    const settings = await this.getOrCreateDefaultSettings();
    const result = await this.update(settings.id, updates as Partial<LiveTradingSettings>);

    // Notify same-tab subscribers (e.g., useAlarmEngine in LiveTradingPhase)
    syncCoordinatorService.notifyLocalChange(this.tableName, settings.id);
    // Notify other tabs via BroadcastChannel (self-bounce guard prevents double-fire)
    syncCoordinatorService.notifyChange(this.tableName, settings.id);

    return result;
  }

  /**
   * Update quick-trade-related settings (singleton write API)
   *
   * Resolves the singleton settings record internally. Accepts any subset of
   * showQuickTrades, quickTradePresets, activeTradesDefaultSort, activeTradesCardInfo.
   *
   * @param updates - Fields to update
   * @returns Promise resolving to updated LiveTradingSettings
   */
  async updateQuickTradesSettings(
    updates: Partial<Pick<LiveTradingSettings, 'showQuickTrades' | 'quickTradePresets' | 'activeTradesDefaultSort' | 'activeTradesCardInfo'>>,
  ): Promise<LiveTradingSettings> {
    const settings = await this.getOrCreateDefaultSettings();
    return this.update(settings.id, updates as Partial<LiveTradingSettings>);
  }
}

// Export singleton instance for use throughout the application
export const liveTradingSettingsService = new LiveTradingSettingsService();
